import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import Pagination from "../../../components/common/Pagination";

const PopupModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[640px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[22px] right-[20px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        <div className="p-6">
          <h3 className="text-[18px] font-semibold text-[#353535] mb-4">
            {title}
          </h3>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Generic CRUD tab used inside Admin Settings (Cuisine Type / Special Features)
 * Keeps styling consistent with existing admin tables/modals.
 */
const SettingsCrudTab = ({
  title,
  addLabel,
  queryKeyBase,
  limit = 10,
  fetchFn,
  addFn,
  updateFn,
  deleteFn,
  // how to read list + pagination from API response
  getItemsFromResponse,
  getPaginationFromResponse,
}) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [queryKeyBase, page],
    queryFn: () => fetchFn({ page, limit }),
    keepPreviousData: true,
  });

  const items = useMemo(() => {
    try {
      return getItemsFromResponse?.(data) || [];
    } catch {
      return [];
    }
  }, [data, getItemsFromResponse]);

  const pagination = useMemo(() => {
    try {
      return getPaginationFromResponse?.(data) || {};
    } catch {
      return {};
    }
  }, [data, getPaginationFromResponse]);

  const { mutate: addMutation, isPending: isAdding } = useMutation({
    mutationFn: addFn,
    onSuccess: () => {
      toast.success("Added successfully");
      queryClient.invalidateQueries([queryKeyBase]);
      closeModal();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Failed to add"),
  });

  const { mutate: updateMutation, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, data }) => updateFn(id, data),
    onSuccess: () => {
      toast.success("Updated successfully");
      queryClient.invalidateQueries([queryKeyBase]);
      closeModal();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Failed to update"),
  });

  const { mutate: deleteMutation } = useMutation({
    mutationFn: deleteFn,
    onSuccess: () => {
      toast.success("Deleted successfully");
      queryClient.invalidateQueries([queryKeyBase]);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Failed to delete"),
  });

  const openAdd = () => {
    setEditItem(null);
    setName("");
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setName(item?.name || item?.title || "");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditItem(null);
    setName("");
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Name is required");

    if (editItem?._id || editItem?.id) {
      updateMutation({
        id: editItem?._id || editItem?.id,
        data: {
          name: trimmed,
          // preserve existing active flag if present, otherwise default true
          isActive: typeof editItem?.isActive === "boolean" ? editItem.isActive : true,
        },
      });
    } else {
      addMutation({ name: trimmed, isActive: true });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) deleteMutation(id);
    });
  };

  return (
    <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[18px] py-[20px] sm:px-[30px] px-[15px] bg-white">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-[#18181B]">{title}</h3>
        <button
          onClick={openAdd}
          className="inline-block rounded-[2px] bg-[#4CAF50] text-white font-semibold px-4 py-2 green-shadow hover:bg-[#209b25]"
        >
          {addLabel}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isError ? (
        <p className="text-red-500 text-center mt-6">
          {error?.message || "Failed to load"}
        </p>
      ) : (
        <div className="overflow-x-auto mt-[18px]">
          <table className="min-w-full">
            <thead className="bg-[#FFFAF2]">
              <tr>
                <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                  Name
                </th>
                <th className="text-right text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {items?.length ? (
                items.map((item) => (
                  <tr key={item?._id || item?.id} className="border-t">
                    <td className="py-[12px] px-[16px] text-[14px] font-normal text-black">
                      {item?.name || item?.title || "-"}
                    </td>
                    <td className="py-[12px] px-[16px]">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(item)}
                          className="w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] hover:bg-[#eaeaea]"
                          aria-label="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(item?._id || item?.id)}
                          className="w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] hover:bg-[#ffecec] text-[#FF0000]"
                          aria-label="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={2}
                    className="py-6 text-center text-[#667085]"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pagination?.totalPages > 0 && (
        <Pagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 1}
          setPage={setPage}
        />
      )}

      <PopupModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editItem ? `Update ${title}` : `Add ${title}`}
      >
        <div className="flex flex-col gap-3">
          <label className="text-sm text-[#000000] mb-[2px] block">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter name"
            className="border border-[#DDDDDD] rounded-[10px] p-[12px] w-full focus:outline-none focus:border-[#4CAF50]"
          />

          <div className="flex gap-3 mt-3">
            <button
              onClick={handleSave}
              disabled={isAdding || isUpdating}
              className="flex-1 bg-[#4CAF50] text-white font-semibold py-2 rounded-[8px] shadow-sm hover:bg-[#43A047] transition-colors disabled:opacity-60"
            >
              {editItem ? "Update" : "Add"}
            </button>
            <button
              onClick={closeModal}
              className="flex-1 border border-[#FF9D9D] text-[#FF0000] font-semibold py-2 rounded-[8px] hover:bg-[#FFF2F2] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </PopupModal>
    </div>
  );
};

export default SettingsCrudTab;

